import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { slideUp } from '@/lib/motion';
import { phantomProps } from '@/config/phantom';
import { useTheme } from '@/contexts/ThemeContext';
import {
  useProgram,
  useUpdateProgramStatus,
  useDeleteProgram,
} from '../../services/programs';
import { useImageUrl } from '../../services/uploads';
import type { ProgramStatus, ProgramDayWithCourses, ProgramAudience } from '../../types/program';
import ProgramStatusBadge from '../../components/programs/ProgramStatusBadge';
import ActionMenu, { ActionMenuItem } from '../../components/common/ActionMenu';
import SettingsIcon from '../../components/icons/SettingsIcon';
import PencilIcon from '../../components/icons/PencilIcon';
import HiddenIcon from '../../components/icons/HiddenIcon';
import ErrorState from '../../components/common/ErrorState';
import CommentsSection from '../../components/comments/CommentsSection';
import Modal from '../../components/common/Modal';
import {
  TrashIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  AcademicCapIcon,
  BookOpenIcon,
  ClockIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { CheckBadgeIcon } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';
import ActivateSwapDialog from '@/components/programs/ActivateSwapDialog';

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}



// ─── Sub-components ──────────────────────────────────────────────────────────

interface CourseThumbProps {
  imageKey: string | null | undefined;
  alt: string;
}

const CourseThumb: React.FC<CourseThumbProps> = ({ imageKey, alt }) => {
  const [resolvedUrl] = useImageUrl(imageKey ?? null);
  return (
    <div className="w-10 h-10 flex-shrink-0 rounded-lg overflow-hidden bg-background-secondary">
      {resolvedUrl ? (
        <img src={resolvedUrl} alt={alt} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <BookOpenIcon className="w-4 h-4 text-text-tertiary" />
        </div>
      )}
    </div>
  );
};

interface DayCardProps {
  day: ProgramDayWithCourses;
}

const DayCard: React.FC<DayCardProps> = ({ day }) => {
  const [expanded, setExpanded] = useState(false);
  const [imageUrl] = useImageUrl(day.header_image);

  return (
    <div className="card overflow-hidden">
      {/* Day header */}
      <button
        onClick={() => setExpanded((prev) => !prev)}
        className="w-full flex items-center gap-4 p-4 text-left hover:bg-background-secondary/50 transition-colors"
        aria-expanded={expanded}
      >
        {/* Day image thumbnail */}
        <div className="w-14 h-14 flex-shrink-0 rounded-xl overflow-hidden bg-background-secondary">
          {imageUrl ? (
            <img src={imageUrl} alt={day.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <CalendarDaysIcon className="w-6 h-6 text-text-tertiary" />
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-text-tertiary uppercase tracking-wide mb-0.5">
            Day {day.day_number}
          </p>
          <h4 className="text-sm font-semibold text-text-primary truncate">{day.title}</h4>
          <p className="text-xs text-text-secondary mt-0.5">
            {day.courses.length} {day.courses.length === 1 ? 'course' : 'courses'}
          </p>
        </div>

        <div className="flex-shrink-0 text-text-tertiary">
          {expanded ? (
            <ChevronDownIcon className="w-4 h-4" />
          ) : (
            <ChevronRightIcon className="w-4 h-4" />
          )}
        </div>
      </button>

      {/* Expanded course list */}
      {expanded && (
        <div className="border-t border-border-primary">
          {day.courses.length === 0 ? (
            <p className="px-4 py-3 text-sm text-text-tertiary italic">No courses added yet.</p>
          ) : (
            <ul className="divide-y divide-border-primary">
              {day.courses.map((dc) => (
                <li key={dc.id} className="flex items-center gap-3 px-4 py-3">
                  {/* Course thumbnail */}
                  <CourseThumb
                    imageKey={dc.course?.image_url ?? null}
                    alt={dc.course?.title ?? ''}
                  />

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-primary truncate">
                      {dc.course?.title ?? 'Untitled course'}
                    </p>
                    {dc.course?.estimated_duration_minutes != null && (
                      <p className="flex items-center gap-1 text-xs text-text-secondary mt-0.5">
                        <ClockIcon className="w-3 h-3" />
                        {dc.course.estimated_duration_minutes} min
                      </p>
                    )}
                  </div>

                  <span className="flex-shrink-0 text-xs px-2 py-0.5 rounded-full bg-background-secondary text-text-secondary capitalize">
                    {dc.course?.status ?? ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

interface AudienceCardProps {
  audience: ProgramAudience;
}

const AudienceCard: React.FC<AudienceCardProps> = ({ audience }) => {
  return (
    <div className="card p-4 flex flex-col gap-3" data-testid="audience-card">
      {/* Name */}
      <div className="flex items-start justify-between gap-2">
        <h4 className="text-sm font-semibold text-text-primary">
          {audience.name ?? 'Unnamed audience'}
        </h4>
        <span className="flex items-center gap-1 text-xs font-medium text-text-secondary bg-background-secondary px-2 py-0.5 rounded-full flex-shrink-0">
          <UserGroupIcon className="w-3.5 h-3.5" />
          {audience.learners_count} {audience.learners_count === 1 ? 'learner' : 'learners'}
        </span>
      </div>

      {/* Date range */}
      <div className="flex flex-wrap gap-3 text-xs text-text-secondary">
        <div>
          <span className="font-medium text-text-primary">Start:</span>{' '}
          {formatDate(audience.start_date)}
        </div>
        <div>
          <span className="font-medium text-text-primary">End:</span>{' '}
          {formatDate(audience.end_date)}
        </div>
        {audience.publish_date && (
          <div>
            <span className="font-medium text-text-primary">Publish:</span>{' '}
            {formatDate(audience.publish_date)}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Tab navigation ──────────────────────────────────────────────────────────

type TabId = 'overview' | 'days' | 'audiences';

const TABS: { id: TabId; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'days', label: 'Days' },
  { id: 'audiences', label: 'Audiences' },
];

// ─── Header image component ──────────────────────────────────────────────────

interface ProgramHeaderImageProps {
  headerImageKey: string | null;
  name: string;
}

const ProgramHeaderImage: React.FC<ProgramHeaderImageProps> = ({ headerImageKey, name }) => {
  const [imageUrl] = useImageUrl(headerImageKey);

  if (!imageUrl) {
    return (
      <div className="w-full h-64 sm:h-72 rounded-2xl bg-gradient-to-br from-primary-400 to-primary-700 flex items-center justify-center">
        <AcademicCapIcon className="w-16 h-16 text-white/60" />
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={name}
      className="w-full h-64 sm:h-72 rounded-2xl object-cover"
      data-testid="program-header-image"
    />
  );
};

// ─── Main component ──────────────────────────────────────────────────────────

const ProgramDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { theme } = useTheme();

  const { data: program, isLoading, error, refetch } = useProgram(id);

  const updateStatusMutation = useUpdateProgramStatus();
  const deleteMutation = useDeleteProgram();

  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [statusPending, setStatusPending] = useState(false);
  // Set when activation is refused for hitting the plan's active-program cap;
  // carries the server's message into the swap dialog.
  const [swapLimitMessage, setSwapLimitMessage] = useState<string | null>(null);

  const handleStatusTransition = async (toStatus: ProgramStatus) => {
    if (!id || statusPending) return;
    setStatusPending(true);
    try {
      await updateStatusMutation.mutateAsync({ programId: id, status: toStatus });
      toast.success(
        `Program ${
          toStatus === 'active'
            ? 'published'
            : toStatus === 'draft'
              ? 'moved to drafts'
              : 'hidden'
        } successfully.`
      );
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { error?: string; message?: string } } })
        ?.response?.data;
      // At the cap, offer the swap instead of a dead end — same escape hatch
      // courses have had since August (Aleix, 2026-09-08).
      if (toStatus === 'active' && data?.error === 'PLAN_LIMIT_REACHED') {
        setSwapLimitMessage(data.message ?? 'Your plan is at its active-program limit.');
        return;
      }
      toast.error('Failed to update program status. Please try again.');
    } finally {
      setStatusPending(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      await deleteMutation.mutateAsync(id);
      toast.success('Program deleted successfully.');
      navigate('/programs');
    } catch {
      toast.error('Failed to delete program. Please try again.');
      setDeleteModalOpen(false);
    }
  };

  // ─── Error / not found ─────────────────────────────────────────────────────
  if (!isLoading && (error || !program)) {
    return (
      <div className="max-w-5xl mx-auto">
        <ErrorState
          title="Program Not Found"
          message="The learning program you're looking for doesn't exist or you don't have permission to view it."
          onRetry={() => refetch()}
          action={{
            label: 'Back to Programs',
            onClick: () => navigate('/programs'),
          }}
        />
      </div>
    );
  }

  // 'disabled' is a legacy state nothing sets any more — historical rows
  // still read as hidden (Aleix, 2026-09-09).
  const hidden = program?.status === 'archived' || program?.status === 'disabled';
  const programMenuItems: ActionMenuItem[] = [
    {
      label: 'Edit',
      icon: <PencilIcon className="w-4 h-4" color="currentColor" accentColor={'rgb(var(--color-primary-100))'} />,
      onClick: () => navigate(`/programs/${id}/edit`),
      'data-testid': 'program-menu-edit',
    },
    {
      label: 'Configure',
      icon: <SettingsIcon className="w-4 h-4" color="currentColor" accentColor={'rgb(var(--color-primary-100))'} />,
      onClick: () => navigate(`/programs/${id}/configure`),
      'data-testid': 'program-menu-configure',
    },
    {
      label: hidden ? 'Unhide' : 'Hide',
      icon: <HiddenIcon className="w-4 h-4" color="currentColor" accentColor={'rgb(var(--color-primary-100))'} />,
      onClick: () => handleStatusTransition(hidden ? 'draft' : 'archived'),
      'data-testid': 'program-menu-hide',
    },
    {
      label: 'Delete',
      icon: <TrashIcon className="w-4 h-4" />,
      onClick: () => setDeleteModalOpen(true),
      isDestructive: true,
      separatorAbove: true,
      'data-testid': 'program-menu-delete',
    },
  ];

  return (
    <motion.div
      className="max-w-5xl mx-auto space-y-6"
      data-testid="program-detail-page"
      {...slideUp}
    >
      {swapLimitMessage !== null && program && (
        <ActivateSwapDialog
          programId={program.id}
          programName={program.name}
          limitMessage={swapLimitMessage}
          onClose={() => setSwapLimitMessage(null)}
          onSwapped={() => refetch()}
        />
      )}
      <phantom-ui {...phantomProps(theme === 'dark', isLoading)}>
        {/* Breadcrumb path — PageHeader style (this page has a hero header card) */}
        <nav aria-label="Breadcrumb" className="flex items-center gap-1.5" data-testid="breadcrumbs">
          <Link
            to="/dashboard"
            className="body-small text-text-secondary hover:text-primary-600 dark:hover:text-primary-350 transition-colors"
          >
            Dashboard
          </Link>
          <span className="body-small text-text-secondary">•</span>
          <Link
            to="/programs"
            className="body-small text-text-secondary hover:text-primary-600 dark:hover:text-primary-350 transition-colors"
          >
            Learning Programs
          </Link>
          <span className="body-small text-text-secondary">•</span>
          <span className="body-small text-primary-600 dark:text-primary-350 font-medium truncate max-w-[240px]">
            {program?.name ?? 'Program'}
          </span>
        </nav>

        {/* ── Header card ─────────────────────────────────────────────────── */}
        <div className="card p-6">
          <div className="flex flex-col sm:flex-row sm:items-start gap-4">
            {/* Title + status */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap mb-1">
                <h1 className="text-2xl font-bold text-text-primary truncate">
                  {program?.name ?? 'Loading…'}
                </h1>
                {program && <ProgramStatusBadge status={program.status} />}
              </div>
            </div>

            {/* Actions — mirrors the course card: Preview + a settings
                menu with Edit / Configure / Hide / Delete. Publishing a
                draft keeps its own explicit button. */}
            <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
              {program?.status === 'draft' && (
                <button
                  onClick={() => handleStatusTransition('active')}
                  disabled={statusPending}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-primary-500 rounded-full hover:bg-primary-600 dark:hover:bg-primary-400 disabled:opacity-60 active:scale-[0.97] transition-[background-color,transform] duration-150"
                  data-testid="activate-program-btn"
                >
                  Publish
                </button>
              )}

              <button
                onClick={() => navigate(`/programs/${id}/preview`)}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-primary-600 dark:text-primary-300 border border-primary-500/40 rounded-full hover:bg-primary-500/[0.06] active:scale-[0.97] transition-[background-color,transform] duration-150"
                data-testid="preview-program-btn"
                title="Preview as learner"
              >
                <EyeIcon className="w-4 h-4" />
                Preview
              </button>

              <ActionMenu
                items={programMenuItems}
                placement="bottom"
                align="right"
                data-testid="program-actions-menu"
              >
                {({ open, toggle }) => (
                  <button
                    type="button"
                    onClick={toggle}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    aria-label="Program actions"
                    className="flex items-center justify-center w-10 h-10 bg-primary-500/[0.07] border border-border-primary/40 rounded-full hover:bg-primary-500/[0.15] active:scale-[0.94] transition-[background-color,transform] duration-150"
                    data-testid="program-actions-menu-btn"
                  >
                    <SettingsIcon className="w-4 h-4" color="currentColor" accentColor={'rgb(var(--color-primary))'} />
                  </button>
                )}
              </ActionMenu>
            </div>
          </div>
        </div>

        {/* ── Tab bar ─────────────────────────────────────────────────────── */}
        <div className="flex items-center gap-1 border-b border-border-primary">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-text-secondary hover:text-text-primary'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Tab content ─────────────────────────────────────────────────── */}

        {/* Overview tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6 max-w-3xl mx-auto w-full" data-testid="tab-overview-content">
            {/* Header image */}
            {program && (
              <ProgramHeaderImage
                headerImageKey={program.header_image}
                name={program.name}
              />
            )}

            {/* At-a-glance stats — everything else lives in Edit/Configure. */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="program-overview-stats">
              <div className="card p-5 flex items-center gap-3">
                <CalendarDaysIcon className="w-8 h-8 text-primary-500 shrink-0" />
                <div>
                  <p className="text-2xl font-bold text-text-primary leading-tight">
                    {program ? program.duration_days : '—'}
                  </p>
                  <p className="text-xs text-text-secondary uppercase tracking-wide">
                    {program?.duration_days === 1 ? 'Day' : 'Days'}
                  </p>
                </div>
              </div>
              <div className="card p-5 flex items-center gap-3">
                <UserGroupIcon className="w-8 h-8 text-primary-500 shrink-0" />
                <div>
                  <p className="text-2xl font-bold text-text-primary leading-tight">
                    {program ? program.audiences_count : '—'}
                  </p>
                  <p className="text-xs text-text-secondary uppercase tracking-wide">
                    {program?.audiences_count === 1 ? 'Audience' : 'Audiences'}
                  </p>
                </div>
              </div>
              <div className="card p-5 flex items-center gap-3">
                <CheckBadgeIcon className="w-8 h-8 text-primary-500 shrink-0" />
                <div className="min-w-0">
                  {program && <ProgramStatusBadge status={program.status} />}
                  <p className="text-xs text-text-secondary uppercase tracking-wide mt-1">Status</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Days tab */}
        {activeTab === 'days' && (
          <div data-testid="tab-days-content">
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="card h-20 rounded-2xl" />
                ))}
              </div>
            ) : !program || program.days.length === 0 ? (
              <div className="card p-10 text-center">
                <CalendarDaysIcon className="w-12 h-12 mx-auto text-text-tertiary mb-3" />
                <h3 className="text-base font-semibold text-text-primary mb-1">No Days Configured</h3>
                <p className="text-sm text-text-secondary mb-4">
                  This program doesn't have any days set up yet.
                </p>
                <button
                  onClick={() => navigate(`/programs/${id}/edit`)}
                  className="px-4 py-2 text-sm font-semibold bg-primary-500 rounded-full hover:bg-primary-600 dark:hover:bg-primary-400 transition-colors"
                >
                  Configure days
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {program.days
                  .slice()
                  .sort((a, b) => a.day_number - b.day_number)
                  .map((day) => (
                    <DayCard key={day.id} day={day} />
                  ))}
              </div>
            )}
          </div>
        )}

        {/* Audiences tab */}
        {activeTab === 'audiences' && (
          <div data-testid="tab-audiences-content">
            {isLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[1, 2].map((i) => (
                  <div key={i} className="card h-28 rounded-2xl" />
                ))}
              </div>
            ) : !program || program.audiences.length === 0 ? (
              <div className="card p-10 text-center">
                <UserGroupIcon className="w-12 h-12 mx-auto text-text-tertiary mb-3" />
                <h3 className="text-base font-semibold text-text-primary mb-1">No Audiences Yet</h3>
                <p className="text-sm text-text-secondary mb-4">
                  Assign audiences to this program to enroll learners.
                </p>
                <button
                  onClick={() => navigate(`/programs/${id}/configure`)}
                  className="px-4 py-2 text-sm font-semibold bg-primary-500 rounded-full hover:bg-primary-600 dark:hover:bg-primary-400 active:scale-[0.97] transition-[background-color,transform] duration-150"
                >
                  Add audiences
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {program.audiences.map((audience) => (
                  <AudienceCard key={audience.id} audience={audience} />
                ))}
              </div>
            )}
          </div>
        )}
      </phantom-ui>

      {/* ── Delete confirmation modal ──────────────────────────────────── */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Program"
        size="sm"
        data-testid="delete-confirm-modal"
        footer={
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={() => setDeleteModalOpen(false)}
              className="px-4 py-2 text-sm font-semibold text-text-secondary border border-border-primary rounded-full hover:bg-background-secondary transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="px-4 py-2 text-sm font-semibold text-white bg-error-600 rounded-full hover:bg-error-700 transition-colors disabled:opacity-60"
              data-testid="confirm-delete-btn"
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        }
      >
        <p className="text-sm text-text-secondary">
          Are you sure you want to delete{' '}
          <span className="font-semibold text-text-primary">{program?.name}</span>? This action
          cannot be undone.
        </p>
      </Modal>

      {/* Same conversation the learners are having, in front of the people who
          can act on it (Aleix, 2026-09-15 — courses and programs alike). */}
      {id && (
        <div className="mt-10">
          <CommentsSection targetType="program" targetId={id} />
        </div>
      )}
    </motion.div>
  );
};

export default ProgramDetailPage;
